/**
 * Autocomplete Client using autosuggest-sdk
 * 
 * This module wraps the autosuggest-sdk to provide autocomplete functionality
 * for hotels. The SDK handles normalization internally.
 */

import { createAutosuggestClient } from '@datadirects/autosuggest-sdk';
import type { IAirportItem } from '../components/Common/AirportSelect/Item';

/**
 * Determine environment from NEXT_PUBLIC_SENTRY_ENV or NEXT_PUBLIC_BACKEND_URL
 */
function getEnvironment(): 'acpt' | 'int' | 'prod' {
	// Check SENTRY_ENV first
	const sentryEnv = process.env.NEXT_PUBLIC_SENTRY_ENV?.toLowerCase();
	if (sentryEnv === 'prod' || sentryEnv === 'production') {
		return 'prod';
	}
	if (sentryEnv === 'int' || sentryEnv === 'integration') {
		return 'int';
	}
	if (sentryEnv === 'acpt' || sentryEnv === 'acceptance' || sentryEnv === 'dev') {
		return 'acpt';
	}

	// Fallback: check BACKEND_URL
	const backendUrl = process.env.NEXT_PUBLIC_BACKEND_URL || '';
	if (backendUrl.includes('.prod.') || backendUrl.includes('searchhotelprices.com')) {
		return 'prod';
	}
	if (backendUrl.includes('.int.')) {
		return 'int';
	}

	// Default to acpt for development
	return 'acpt';
}

// Create the autosuggest client with configuration
// Environment is determined automatically from NEXT_PUBLIC_SENTRY_ENV:
// - prod → production API
// - int → integration API
// - acpt/dev → acceptance API (default)
const client = createAutosuggestClient({
	mode: 'new',
	env: getEnvironment(),
	timeoutMs: 5000,
	debounceMs: 300,
	logger: (event) => {
		console.log('[Autosuggest SDK]', {
			source: event.source,
			query: event.query,
			status: event.status,
			responseTime: event.responseTimeMs ? `${event.responseTimeMs}ms` : 'N/A',
			error: event.error,
		});
	},
});

/**
 * Map raw primaryPlaceType to kayakType used by hotelActions/baseActions.
 * - city/town → 'city'
 * - hotel → 'hotel'
 * - airport → 'ap'
 */
function mapKayakType(primaryPlaceType: string): string {
	switch (primaryPlaceType) {
		case 'city':
		case 'town':
			return 'city';
		case 'hotel':
			return 'hotel';
		case 'airport':
			return 'ap';
		case 'landmark':
			return 'landmark';
		default:
			return 'city';
	}
}

/**
 * Build ptid in the format expected by hotelActions/baseActions.
 * hotelActions does: 'C:' + destination.ptid.substr(1) for cities
 *                    'H:' + destination.ptid.substr(1) for hotels
 * So we need:
 * - cities:  "c<cityId>"  → substr(1) yields "<cityId>"  → "C:<cityId>"
 * - hotels:  "h<hotelId>" → substr(1) yields "<hotelId>" → "H:<hotelId>"
 */
function buildPtid(primaryPlaceType: string, cityId?: number, hotelId?: number, placeId?: number): string {
	switch (primaryPlaceType) {
		case 'city':
		case 'town':
			return cityId != null ? `c${cityId}` : '';
		case 'hotel':
			return hotelId != null ? `h${hotelId}` : '';
		case 'landmark':
			return placeId != null ? `p${placeId}` : '';
		default:
			return '';
	}
}

/**
 * Fetch autocomplete suggestions for hotels
 *
 * @param query - The search query
 * @param locale - Optional locale code (e.g., 'en-US', 'de-DE')
 * @param signal - Optional AbortSignal for request cancellation
 * @returns Array of hotel/city items in IAirportItem format
 */
export async function fetchHotelAutocomplete(
	query: string,
	locale?: string,
	signal?: AbortSignal
): Promise<IAirportItem[]> {
	try {
		const response = await client.fetch({
			query,
			locale: locale || 'en-US',
			vertical: 'hotels',
			normalizeResponse: false,
			signal,
		});

		// Raw response: { source: 'new', vertical, data: { results: [...] } }
		const data = response.data as any;
		const results = data?.results;
		if (!results || !Array.isArray(results)) {
			return [];
		}

		// Map raw autosuggest API items to IAirportItem format
		const mappedResults = results.map((item: any) => {
			const primaryPlaceType = item.primaryPlaceType || 'city';
			const kayakType = mapKayakType(primaryPlaceType);

			let type = 'city';
			if (kayakType === 'ap') {
				type = 'airport';
			} else if (kayakType === 'hotel') {
				type = 'hotel';
			} else if (kayakType === 'landmark') {
				type = 'landmark';
			}

			const ptid = buildPtid(primaryPlaceType, item.cityId, item.hotelId, item.placeId);
			let destcode: string | undefined;
			if (primaryPlaceType === 'city' || primaryPlaceType === 'town') {
				if (item.cityId != null) {
					destcode = `C:${item.cityId}`;
				}
			} else if (primaryPlaceType === 'hotel') {
				if (item.hotelId != null) {
					destcode = `H:${item.hotelId}`;
				}
			} else if (primaryPlaceType === 'landmark') {
				if (item.placeId != null) {
					destcode = `P:${item.placeId}`;
				}
			} else if (primaryPlaceType === 'airport' && item.iataCode) {
				destcode = String(item.iataCode).toUpperCase();
			}

			const placeId =
				item.placeId != null && String(item.placeId).trim() !== ''
					? String(item.placeId).trim()
					: undefined;

			return {
				name: item.name || item.cityName || '',
				country: item.countryName || '',
				city: item.cityName || '',
				state: item.regionName || '',
				iata: item.iataCode || '',
				type,
				documentId: item.entityKey || `${primaryPlaceType}_${item.placeId}` || '',
				cityID: item.cityId != null ? String(item.cityId) : '',
				airportID: '',
				allAirports: false,
				kayakType,
				ptid,
				placeId,
				countryCode: item.countryCode || '',
				stateCode: item.regionName || '',
				destcode,
			} as IAirportItem;
		});

		return mappedResults;
	} catch (error) {
		console.error('Autocomplete SDK error:', error);
		return [];
	}
}

export default {
	fetchHotelAutocomplete,
};
